import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import esES from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem, TextField, Grid, Alert, Typography } from '@mui/material';

const locales = { 'es': esES };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales,
});

const MACHINE_COLORS = [
  '#FF5733', // Rojo anaranjado
  '#33C1FF', // Azul claro
  '#28A745', // Verde intenso
  '#FFC300', // Amarillo fuerte
  '#8E44AD', // Morado
  '#FF33A8', // Fucsia
  '#34495E', // Azul grisáceo oscuro
  '#FFB833', // Naranja claro
  '#00B894', // Verde agua
  '#E17055', // Coral
  '#2D3436', // Gris oscuro
  '#00CEC9', // Turquesa
  '#6D214F', // Vino
  '#B33771', // Magenta oscuro
  '#218C5A'  // Verde bosque
];

function getColorForMachine(machineNumber) {
  if (!machineNumber) return MACHINE_COLORS[0];
  let str = String(machineNumber).trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return MACHINE_COLORS[Math.abs(hash) % MACHINE_COLORS.length];
}

const AlmanaqueOperarios = () => {
  const [turnosDefinidos, setTurnosDefinidos] = useState(null);
  const [alertaTurnos, setAlertaTurnos] = useState('');
  const [events, setEvents] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [operariosList, setOperariosList] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ dispositivoId: '', turnos: {}, note: '' });
  const [editingEvent, setEditingEvent] = useState(null);
  const [error, setError] = useState(null);
  const [horariosTurnos, setHorariosTurnos] = useState({});

  // Consulta los turnos definidos para la fecha seleccionada
  const verificarTurnosParaFecha = async (fecha) => {
    try {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/turnos-por-fecha?fecha=${fecha}`);
      const data = await response.json();
      if (!data.turnos || data.turnos.length === 0) {
        setAlertaTurnos('Debes crear los turnos para esta fecha antes de asignar operarios.');
        setTurnosDefinidos(null);
      } else {
        setAlertaTurnos('');
        setTurnosDefinidos(data.turnos);
      }
    } catch (error) {
      setAlertaTurnos('Error consultando turnos para la fecha.');
      setTurnosDefinidos(null);
    }
  };

  // Carga datos iniciales
  const fetchInitialData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [machinesRes, operatorsRes, assignmentsRes] = await Promise.all([
        fetch('/api/machines', { headers }),
        fetch('/api/operators', { headers }),
        fetch('/api/asignar-operario', { headers })
      ]);

      const [machinesData, operatorsData, assignmentsData] = await Promise.all([
        machinesRes.json(),
        operatorsRes.json(),
        assignmentsRes.json()
      ]);

      setMaquinas(Array.isArray(machinesData) ? machinesData : []);
      setOperariosList(Array.isArray(operatorsData) ? operatorsData : []);

      // Mapeo de eventos con nombres de operario y color por máquina
      const mappedEvents = (Array.isArray(assignmentsData) ? assignmentsData : []).map(asig => {
        return {
          id: asig._id,
          title: `${asig.dispositivoId || 'N/A'} | ${asig.turnos.map(t => {
            const op = operatorsData.find(op => op._id === (t.operario?._id || t.operario));
            return `T${t.turno}: ${op ? `${op.name} ${op.lastName}` : t.operario}`;
          }).join(' | ')}`,
          start: new Date(asig.fecha),
          end: new Date(asig.fecha),
          allDay: true,
          raw: asig,
          color: getColorForMachine(asig.dispositivoId || asig.machineNumber)
        };
      });
      setEvents(mappedEvents);
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setError("No se pudieron cargar los datos iniciales. Por favor, intente de nuevo.");
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Cuando cambian los turnos definidos y el formulario está abierto, actualiza los inputs de turnos y horarios
  useEffect(() => {
    if (open && turnosDefinidos) {
      const turnosObj = {};
      const horariosObj = {};
      turnosDefinidos.forEach((turno, idx) => {
        turnosObj[idx + 1] = '';
        horariosObj[idx + 1] = {
          fechaInicio: turno.inicio || '',
          fechaFin: turno.fin || ''
        };
      });
      setForm(f => ({ ...f, turnos: turnosObj }));
      setHorariosTurnos(horariosObj);
    }
  }, [turnosDefinidos, open]);

  // Al seleccionar un slot en el calendario
  const handleSelectSlot = ({ start }) => {
    const fecha = format(start, 'yyyy-MM-dd');
    verificarTurnosParaFecha(fecha); // Esto actualizará turnosDefinidos
    setSelectedDate(start);
    setForm({ dispositivoId: '', turnos: {}, note: '' }); // Inicializa vacío, se llenará en useEffect
    setEditingEvent(null);
    setError(null);
    setOpen(true);
  };

  // Al seleccionar un evento existente
 const handleSelectEvent = async (event) => {
    setSelectedDate(event.start);
    setEditingEvent(event);
    setError(null);
    setOpen(true);

    // Consulta los turnos definidos para la fecha del evento
    const fecha = format(event.start, 'yyyy-MM-dd');
    await verificarTurnosParaFecha(fecha);

    // Inicializa el formulario con los datos del evento
    setForm({
        dispositivoId: event.raw.dispositivoId,
        turnos: event.raw.turnos.reduce((acc, t) => ({ ...acc, [t.turno]: t.operario?._id || t.operario }), {}),
        note: event.raw.note || ''
    });

    // Inicializa los horarios de los turnos si existen
    const horariosObj = {};
    event.raw.turnos.forEach((t) => {
        horariosObj[t.turno] = {
            fechaInicio: t.fechaInicio || '',
            fechaFin: t.fechaFin || ''
        };
    });
    setHorariosTurnos(horariosObj);
};

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleChangeOperario = (turno, value) => {
    setForm({ ...form, turnos: { ...form.turnos, [turno]: value } });
  };

  // Nuevo: handler para cambiar hora inicio/fin de un turno
  const handleHorarioTurnoChange = (turnoIdx, field, value) => {
    setHorariosTurnos(prev => ({
      ...prev,
      [turnoIdx]: {
        ...prev[turnoIdx],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedDate) {
      setError('No se ha seleccionado una fecha.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('No se encontró un token de autenticación.');
      return;
    }

    // Crea el arreglo de turnos según los turnos definidos y los operarios seleccionados
    const turnosArr = turnosDefinidos
      ? turnosDefinidos.map((turno, idx) => ({
          turno: idx + 1,
          operario: form.turnos[idx + 1] || null,
          fechaInicio: horariosTurnos[idx + 1]?.fechaInicio || '',
          fechaFin: horariosTurnos[idx + 1]?.fechaFin || ''
        })).filter(t => t.operario)
      : [];

    const datos = {
      fecha: selectedDate ? selectedDate.toISOString() : null,
      dispositivoId: form.dispositivoId,
      turnos: turnosArr,
      note: form.note
    };

    try {
      let response;
      if (editingEvent) {
        response = await fetch(`/api/asignar-operario/${editingEvent.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(datos)
        });
      } else {
        response = await fetch('/api/asignar-operario', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(datos)
        });
      }

      if (response.ok) {
        setOpen(false);
        fetchInitialData();
      } else {
        const errorData = await response.json();
        setError(`Error al guardar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      setError('Error de conexión al servidor.');
    }
  };

  const handleDelete = async () => {
    setError(null);
    if (!editingEvent) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/asignar-operario/${editingEvent.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setOpen(false);
        fetchInitialData();
      } else {
        const errorData = await response.json();
        setError(`Error al eliminar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      setError("Error de conexión al servidor al eliminar.");
    }
  };

  return (
    <div style={{ height: 1800, margin: 20 }}>
      {/* Título del calendario */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 12, textAlign: 'center' }}>
        Asignacion de operarios
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        views={['month', 'day', 'agenda']}
        style={{ background: 'rgb(69, 69, 71)', borderRadius: 8 }}
        eventPropGetter={event => ({
          style: {
            backgroundColor: event.color,
            color: '#fff',
            borderRadius: '6px',
            border: 'none'
          }
        })}
        messages={{
          date: 'Fecha',
          time: 'Hora',
          event: 'Evento',
          allDay: 'Todo el día',
          week: 'Semana',
          work_week: 'Semana laboral',
          day: 'Día',
          month: 'Mes',
          previous: 'Anterior',
          next: 'Siguiente',
          yesterday: 'Ayer',
          tomorrow: 'Mañana',
          today: 'Hoy',
          agenda: 'Agenda',
          showMore: total => `+${total} más`
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editingEvent ? 'Editar asignación' : 'Nueva asignación'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {alertaTurnos && (
            <Alert severity="warning" sx={{ mb: 2 }}>{alertaTurnos}</Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Fecha seleccionada"
                value={selectedDate ? selectedDate.toLocaleDateString() : ''}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Máquina"
                value={form.dispositivoId}
                onChange={e => handleChange('dispositivoId', e.target.value)}
                fullWidth
                required
              >
                {maquinas.map(m => (
                  <MenuItem key={m._id} value={m.machineNumber}>{m.machineNumber}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {turnosDefinidos && turnosDefinidos.map((turno, idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={12}>
                  <TextField
                    select
                    label={`Operario para Turno ${idx + 1} (${turno.inicio} - ${turno.fin})`}
                    value={form.turnos[idx + 1] || ''}
                    onChange={e => handleChangeOperario(idx + 1, e.target.value)}
                    fullWidth
                  >
                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                    {operariosList.map(op => (
                      <MenuItem key={op._id} value={op._id}>{op.nombre || op.name || op.username || op.email}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={`Hora inicio turno ${idx + 1}`}
                    type="time"
                    value={horariosTurnos[idx + 1]?.fechaInicio || ''}
                    onChange={e => handleHorarioTurnoChange(idx + 1, 'fechaInicio', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={`Hora fin turno ${idx + 1}`}
                    type="time"
                    value={horariosTurnos[idx + 1]?.fechaFin || ''}
                    onChange={e => handleHorarioTurnoChange(idx + 1, 'fechaFin', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
              </React.Fragment>
            ))}
            <Grid item xs={12}>
              <TextField
                label="Nota"
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {editingEvent && (
            <Button color="error" onClick={handleDelete}>Eliminar</Button>
          )}
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AlmanaqueOperarios;